/**
 * A tenant's offer-PDF watermark (TO-495). pdf-service owns the configuration;
 * the console edits it on both planes and the PDF worker applies it to every
 * render that starts after the save.
 */

/** Which watermark, if any, a tenant's offer PDFs carry. `DISABLED` is the default. */
export enum PdfWatermarkMode {
	DISABLED = 'disabled',
	TEXT = 'text',
	IMAGE = 'image',
}

/** Where an image watermark sits on each page. */
export enum PdfWatermarkPosition {
	CENTER = 'center',
	TOP_LEFT = 'top-left',
	TOP_RIGHT = 'top-right',
	BOTTOM_LEFT = 'bottom-left',
	BOTTOM_RIGHT = 'bottom-right',
}

/** The image types a watermark may be. */
export enum PdfWatermarkImageMimeType {
	PNG = 'image/png',
	JPEG = 'image/jpeg',
}

/**
 * The tokens a text template may contain, written `{name}`. Each resolves to a
 * value of the render being produced (spec BR-3).
 */
export enum PdfWatermarkToken {
	CUSTOMER_NAME = 'customer_name',
	PUBLIC_ID = 'public_id',
	RENDER_VERSION = 'render_version',
	SENT_AT = 'sent_at',
	GENERATED_AT = 'generated_at',
	PDF_CODE = 'pdf_code',
	OFFER_ID = 'offer_id',
}

/** The longest text template, in characters after trimming. */
export const PDF_WATERMARK_TEMPLATE_MAX_LENGTH = 200

/** The largest watermark image, in decoded bytes (256 KiB). */
export const PDF_WATERMARK_IMAGE_MAX_BYTES = 262_144

/**
 * The largest watermark image side, in pixels as declared by its header. Bounds
 * the decoded size, which the byte cap alone does not: a few hundred bytes of
 * PNG/JPEG can declare billions of pixels (a decompression bomb).
 */
export const PDF_WATERMARK_IMAGE_MAX_SIDE_PX = 4096

/** The opacity bounds of an image watermark, inclusive. */
export const PDF_WATERMARK_OPACITY_MIN = 0.05
export const PDF_WATERMARK_OPACITY_MAX = 1

export interface PdfWatermarkTextSettings {
	template: string
	diagonalStamp: boolean
}

export interface PdfWatermarkImageSettings {
	mimeType: PdfWatermarkImageMimeType
	/** Standard, padded base64 of the image bytes. */
	contentBase64: string
	/** The decoded size in bytes. */
	byteSize: number
	opacity: number
	position: PdfWatermarkPosition
}

/** `GET`/`PUT` response of `{base}/pdf-settings/watermark`. */
export interface PdfWatermarkSettings {
	mode: PdfWatermarkMode
	text: PdfWatermarkTextSettings | null
	image: PdfWatermarkImageSettings | null
	/** ISO-8601 UTC of the last save; `null` when nothing was ever saved. */
	updatedAt: string | null
}

/** `PUT` body: a full replacement; the block of the chosen mode only. */
export interface PdfWatermarkSettingsRequest {
	mode: PdfWatermarkMode
	text?: { template: string; diagonalStamp?: boolean }
	image?: {
		mimeType: PdfWatermarkImageMimeType
		contentBase64: string
		opacity: number
		position: PdfWatermarkPosition
	}
}
